import { PayloadAction } from '@reduxjs/toolkit';

import moment from 'moment';

import {
    PatientMedicationDto,
    PatientMedicationIndicationAndFillRequest,
    PatientMedicationNotDoneOrderRequest,
    PatientMedicationPrescriptionDetailsRequest,
    PatientMedicationSaveRequest,
    PatientMedicationsClient,
} from '@medone/medonehp-api-client';

import { handleError } from '../../../../shared/common/HandleErrors';
import { shouldSkipByPatientId } from '../../../../shared/common/helpers';
import { Axios } from '../../../../shared/common/http';
import { AppDispatch, AppThunk, RootState } from '../../../../shared/store';
import { CensusState } from './models';
import { censusSlice } from './slice';

// A save either succeeds (list refetched) or comes back with a readable message for the modal to show inline.
// SCRUM-1489: a success can carry a message too — a code newer than the drug list shipped with Memry is saved
// with a warning — so the message is kept on success rather than dropped as the allergy save does.
export type SavePatientMedicationResult = { succeeded: boolean; message?: string };

// SCRUM-1489 - same rationale as the allergy list (SCRUM-1372): the stale-patient guard reads currentCensus,
// which only the chart maintains, and the clinical coordinator Medical Summary can find it pinned to a patient
// edited earlier in the shift. That discarded the Medical Summary's fetch outright, and with the loading flag
// cleared outside a finally the spinner stuck too — the "medications missing after a reload" of AC-4. Census-less
// hosts opt out; the ownership tag on the list is what keeps that safe.
type PatientMedicationGuardOptions = { bypassActivePatientGuard?: boolean };

const shouldDiscard = (state: RootState, patientId: number, options?: PatientMedicationGuardOptions) =>
    !options?.bypassActivePatientGuard && shouldSkipByPatientId(state.census, patientId);

// After a save or remove, refresh only the list that is still this patient's. With the census guard bypassed (the
// Medical Summary), a write still in flight when the user moves on would otherwise refetch the previous patient,
// re-tag the list to them, and make the new patient's own response drop as stale: the screen would read "No
// medications recorded." and offer No known medications for a patient whose list never loaded.
const shouldRefetchAfterWrite = (state: RootState, patientId: number, options?: PatientMedicationGuardOptions) =>
    !shouldDiscard(state, patientId, options) && state.census.patientMedicationsPatientId === patientId;

export const reducers = {
    setPatientMedicationsLoading: (state: CensusState, action: PayloadAction<boolean>) => {
        state.patientMedicationsLoading = action.payload;
    },

    // The list and the patient it belongs to are written together so they can never disagree. Any write is a fresh
    // start for the list, so it also clears a failure recorded against the previous one.
    setPatientMedications: (state: CensusState, action: PayloadAction<{ patientId: number; medications: PatientMedicationDto[] }>) => {
        state.patientMedications = action.payload.medications;
        state.patientMedicationsPatientId = action.payload.patientId;
        state.patientMedicationsLoadFailed = false;
    },

    // SCRUM-1489 - a failed fetch leaves the list empty and tagged to the patient, which reads exactly like "this
    // patient takes none"; the No known medications flag must not be offered on that.
    setPatientMedicationsLoadFailed: (state: CensusState) => {
        state.patientMedicationsLoadFailed = true;
    },
};

export function fetchPatientMedications(patientId: number, options?: PatientMedicationGuardOptions): AppThunk {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        dispatch(censusSlice.actions.setPatientMedicationsLoading(true));
        // Another patient's rows go before the new fetch resolves: the loading mask is translucent and they would
        // stay readable underneath it. This patient's rows stay put, so a refetch after a save does not flash empty.
        if (getState().census.patientMedicationsPatientId !== patientId) {
            dispatch(censusSlice.actions.setPatientMedications({ patientId, medications: [] }));
        }

        const client = new PatientMedicationsClient(null, Axios);

        try {
            const response = await client.getByPatient(patientId);

            // Guard against race condition: discard if user navigated to a different patient.
            if (shouldDiscard(getState(), patientId, options)) {
                return;
            }

            // With the census guard bypassed nothing else orders two in-flight fetches, so an earlier patient's
            // response could land last and re-tag the list. The tag this fetch set is what makes it current.
            if (getState().census.patientMedicationsPatientId !== patientId) {
                return;
            }

            if (response.result.succeeded) {
                dispatch(censusSlice.actions.setPatientMedications({ patientId, medications: response.result.entity ?? [] }));
            } else {
                dispatch(censusSlice.actions.setPatientMedicationsLoadFailed());
                handleError(response, () => dispatch(censusSlice.actions.setError(null)));
            }
        } catch (error) {
            // Only while the list is still this fetch's: a late failure must not mark a newer patient's list.
            if (getState().census.patientMedicationsPatientId === patientId) {
                dispatch(censusSlice.actions.setPatientMedicationsLoadFailed());
            }
            handleError(error, () => dispatch(censusSlice.actions.setError(error.toString())));
        } finally {
            // In a finally, not after the try: the race guards return early, and a discarded fetch that never
            // cleared the flag left the table spinning for good.
            dispatch(censusSlice.actions.setPatientMedicationsLoading(false));
        }
    };
}

// medicationId null = create, otherwise update. Refetches on success so the server-resolved row lands.
export function savePatientMedication(
    patientId: number,
    medicationId: number | null,
    request: PatientMedicationSaveRequest,
    options?: PatientMedicationGuardOptions
): AppThunk<Promise<SavePatientMedicationResult>> {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const client = new PatientMedicationsClient(null, Axios);

        try {
            const response = medicationId != null ? await client.update(patientId, medicationId, request) : await client.create(patientId, request);

            if (response.result.succeeded) {
                if (shouldRefetchAfterWrite(getState(), patientId, options)) {
                    await dispatch(fetchPatientMedications(patientId, options));
                }

                return { succeeded: true, message: response.result.message || undefined };
            }

            return { succeeded: false, message: response.result.message };
        } catch (error) {
            // Only a 400 carries the server's own rule (ingredient not a clinical drug, already recorded, end before
            // start); a 500's detail is server internals, so it stays behind the generic message.
            const validationDetail = error?.status === 400 ? error?.response?.detail : null;

            // The modal shows this inline; a toast as well would report one failure twice.
            return { succeeded: false, message: validationDetail || 'Something went wrong saving the medication.' };
        }
    };
}

// SCRUM-1517 - the indication and last fill alone: the only write a ScriptSure row takes from the chart, so the
// request carries nothing else. Same result shape and error handling as a full save, since the same modal shows it.
export function savePatientMedicationIndicationAndFill(
    patientId: number,
    medicationId: number,
    request: PatientMedicationIndicationAndFillRequest,
    options?: PatientMedicationGuardOptions
): AppThunk<Promise<SavePatientMedicationResult>> {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const client = new PatientMedicationsClient(null, Axios);

        try {
            const response = await client.updateIndicationAndFill(patientId, medicationId, request);

            if (response.result.succeeded) {
                if (shouldRefetchAfterWrite(getState(), patientId, options)) {
                    await dispatch(fetchPatientMedications(patientId, options));
                }

                return { succeeded: true, message: response.result.message || undefined };
            }

            return { succeeded: false, message: response.result.message };
        } catch (error) {
            // As for a full save: only a 400 carries a rule the user can act on (not an ICD-10-CM code, a fill in the future).
            const validationDetail = error?.status === 400 ? error?.response?.detail : null;

            return { succeeded: false, message: validationDetail || 'Something went wrong saving the medication.' };
        }
    };
}

// Removal has no inline surface (the row's Popconfirm closes either way), so a failure toasts.
export function removePatientMedication(patientId: number, medicationId: number, options?: PatientMedicationGuardOptions): AppThunk {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const client = new PatientMedicationsClient(null, Axios);

        try {
            const response = await client.deleteMedication(patientId, medicationId);

            if (response.result.succeeded) {
                if (shouldRefetchAfterWrite(getState(), patientId, options)) {
                    await dispatch(fetchPatientMedications(patientId, options));
                }
            } else {
                handleError(response, () => dispatch(censusSlice.actions.setError(null)));
            }
        } catch (error) {
            handleError(error, () => dispatch(censusSlice.actions.setError(error.toString())));
        }
    };
}

// SCRUM-1310 — record a coded not-done medication order (measure exclusion/exception). On success we re-fetch so the
// negated row (returned by the GET) replaces the optimistic gap; returns true so the modal can close only on success.
export function createNotDoneMedicationOrder(
    patientId: number,
    request: { rxNormCode: string; drugName: string; reasonSnomedCode: string; serviceDate: moment.Moment },
    options?: PatientMedicationGuardOptions
): AppThunk<Promise<boolean>> {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const client = new PatientMedicationsClient(null, Axios);

        try {
            const response = await client.createNotDoneOrder(patientId, new PatientMedicationNotDoneOrderRequest(request));

            if (response.result.succeeded) {
                // Skip the refetch (and the toast) if the user has since navigated to another patient.
                if (shouldRefetchAfterWrite(getState(), patientId, options)) {
                    await dispatch(fetchPatientMedications(patientId, options));
                }
                return true;
            }

            handleError(response, () => dispatch(censusSlice.actions.setError(null)));
            return false;
        } catch (error) {
            handleError(error, () => dispatch(censusSlice.actions.setError(error.toString())));
            return false;
        }
    };
}

// SCRUM-847 — record prescription and dispense details on an existing row. Same shape as the not-done order:
// re-fetch on success so the list shows what the server stored, and return true so the modal closes only then.
export function updateMedicationPrescriptionDetails(
    patientId: number,
    medicationId: number,
    request: PatientMedicationPrescriptionDetailsRequest,
    options?: PatientMedicationGuardOptions
): AppThunk<Promise<boolean>> {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const client = new PatientMedicationsClient(null, Axios);

        try {
            const response = await client.updatePrescriptionDetails(patientId, medicationId, request);

            if (response.result.succeeded) {
                if (shouldRefetchAfterWrite(getState(), patientId, options)) {
                    await dispatch(fetchPatientMedications(patientId, options));
                }
                return true;
            }

            handleError(response, () => dispatch(censusSlice.actions.setError(null)));
            return false;
        } catch (error) {
            handleError(error, () => dispatch(censusSlice.actions.setError(error.toString())));
            return false;
        }
    };
}


// One shared instance, never an inline []: a fresh array on every render would break the selector's referential
// check and re-render the list on every unrelated census change.
const NO_MEDICATIONS: PatientMedicationDto[] = [];

// SCRUM-1489 - the list and its ownership tag are read together, so there is no ungated way to render the rows:
// the replacing fetch is dispatched from an effect, which commits after the render, so reading the list alone
// paints the previous patient's medications under the new patient's header.
export const selectPatientMedicationsFor = (patientId?: number) => (state: RootState) =>
    state.census.patientMedicationsPatientId === patientId ? state.census.patientMedications : NO_MEDICATIONS;

export const selectPatientMedicationsLoading = (state: RootState) => state.census.patientMedicationsLoading;

export const selectPatientMedicationsLoadFailedFor = (patientId?: number) => (state: RootState) =>
    state.census.patientMedicationsPatientId === patientId && state.census.patientMedicationsLoadFailed;
